import type { LinkDialogState } from '../store/editor-store'
import { useEffect, useRef, useState } from 'react'
import { useEditor } from '../store/context'

export function LinkDialog() {
  const { store } = useEditor()

  const dialogRef = useRef<HTMLDialogElement>(null)
  const urlInputRef = useRef<HTMLInputElement>(null)

  const [url, setUrl] = useState('')
  const [text, setText] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const savedStateRef = useRef<LinkDialogState | null>(null)

  useEffect(() => {
    store.subscribeLinkDialog((state: LinkDialogState) => {
      savedStateRef.current = state
      setUrl(state.url)
      setText(state.text)
      setIsEditing(state.isEditing)
      dialogRef.current!.showModal()
      requestAnimationFrame(() => urlInputRef.current?.focus())
    })
    // eslint-disable-next-line react/exhaustive-deps
  }, [])

  function close() {
    dialogRef.current!.close()
  }

  function apply() {
    if (!url || !savedStateRef.current)
      return
    const ed = store.editor()
    const { from, to, isEditing: editing } = savedStateRef.current
    const hasRange = from !== to
    if (hasRange || editing) {
      ed.chain().focus().setTextSelection({ from, to }).extendMarkRange('link').setLink({ href: url }).run()
    }
    else {
      const display = text.trim() || url
      ed.chain().focus().insertContent(`<a href="${url}">${display}</a>`).run()
    }
    close()
  }

  function remove() {
    store.editor().chain().focus().extendMarkRange('link').unsetLink().run()
    close()
  }

  return (
    <dialog
      ref={dialogRef}
      className="tiptap-dialog"
      onClick={(e) => {
        if (e.target === dialogRef.current)
          close()
      }}
    >
      <p className="dialog-title">{isEditing ? 'Edit link' : 'Insert link'}</p>

      <div className="field">
        <label className="field-label">URL</label>
        <input
          ref={urlInputRef}
          className="field-input"
          type="url"
          placeholder="https://example.com"
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && url)
              apply()
          }}
        />
      </div>

      {!isEditing && (
        <div className="field">
          <label className="field-label">Text (optional)</label>
          <input
            className="field-input"
            type="text"
            placeholder="Link text"
            value={text}
            onChange={e => setText(e.target.value)}
          />
        </div>
      )}

      <div className="dialog-actions">
        <button type="button" className="btn btn-cancel" onClick={close}>Cancel</button>
        {isEditing && (
          <button type="button" className="btn btn-danger" onClick={remove}>Remove</button>
        )}
        <button type="button" className="btn btn-primary" disabled={!url} onClick={apply}>
          {isEditing ? 'Update' : 'Insert'}
        </button>
      </div>
    </dialog>
  )
}
